import { WebGLRenderer } from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

/**
 * KTX2/Basis Universal compressed texture support.
 *
 * KTX2 textures are GPU-compressed, meaning they:
 * - Are smaller to download than JPEG/PNG
 * - Decode faster (GPU-native format, no CPU decode step)
 * - Stay compressed in GPU memory (2-8x less VRAM)
 *
 * Requirements:
 * - Basis transcoder WASM files must be hosted at the configured transcoderPath
 * - KTX2 texture files must be generated from source textures and deployed to CDN
 *
 * Usage:
 * ```typescript
 * const ktx2 = new Ktx2Support();
 * ktx2.initialize(renderer, 'https://cdn.example.com/basis/');
 *
 * // Check if a texture has a KTX2 variant
 * const path = ktx2.getTexturePath('texture.jpg');
 * // Returns 'texture.ktx2' if KTX2 is enabled and variant exists
 *
 * // Load a KTX2 texture
 * const texture = await ktx2.load('https://cdn.example.com/textures/texture.ktx2');
 * ```
 */
// One session-lifetime loader shared by every engine — FileLoader dedups in-flight fetches ACROSS loader instances, so a disposed engine's throwing onLoad would strand every concurrent load of the same URL
let sharedLoader: KTX2Loader | null = null;

export class Ktx2Support {
	#loader: KTX2Loader | null = null;
	#initialized = false;
	#enabled = false;

	/**
	 * Initialize KTX2 support with the WebGL renderer.
	 * Must be called before loading any KTX2 textures.
	 *
	 * @param renderer - WebGL renderer for GPU capability detection
	 * @param transcoderPath - URL path to the Basis transcoder WASM files
	 */
	initialize(renderer: WebGLRenderer, transcoderPath: string): void {
		if (this.#initialized) return;

		try {
			if (!sharedLoader) {
				sharedLoader = new KTX2Loader();
				sharedLoader.setTranscoderPath(transcoderPath);
			}
			sharedLoader.detectSupport(renderer);
			this.#loader = sharedLoader;
			this.#initialized = true;
			this.#enabled = true;
		} catch (e) {
			console.warn('[Ktx2Support] Failed to initialize KTX2 loader:', e);
			this.#loader = null;
			this.#initialized = true;
			this.#enabled = false;
		}
	}

	/**
	 * Whether KTX2 support is available and enabled
	 */
	get isEnabled(): boolean {
		return this.#enabled;
	}

	/**
	 * Get the KTX2Loader instance (null if not initialized or not supported)
	 */
	get loader(): KTX2Loader | null {
		return this.#loader;
	}

	/**
	 * Convert a standard texture filename to its KTX2 variant.
	 * Returns the original filename if KTX2 is not enabled.
	 *
	 * @param filename - Original texture filename (e.g., 'texture.jpg')
	 * @returns KTX2 filename (e.g., 'texture.ktx2') or original if KTX2 not available
	 */
	getKtx2Filename(filename: string): string {
		if (!this.#enabled) return filename;

		// Replace the extension with .ktx2
		const dotIndex = filename.lastIndexOf('.');
		if (dotIndex === -1) return filename;

		return filename.substring(0, dotIndex) + '.ktx2';
	}

	/**
	 * Release this instance's reference. The shared loader (and its transcode workers)
	 * intentionally survives — disposing it while another engine piggybacks the same
	 * FileLoader fetch strands that engine's load forever.
	 */
	dispose(): void {
		this.#loader = null;
		this.#initialized = false;
		this.#enabled = false;
	}
}
